import {
  roleAtLeast,
  type BotCatalogEntry,
  type BotVerificationSummary,
  type TrafficSeriesResponse,
} from '@bartizo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { getServer, listServerGroups, listServers } from '../api/servers.js';
import {
  getServerWebRules,
  getTrafficSeries,
  listBotVerifications,
  type TrafficScope,
} from '../api/traffic.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ActionDialog } from '../components/ActionDialog.js';
import { BotRuleDialog, type BotRuleAction } from '../components/BotRuleDialog.js';
import { BotRulesCard } from '../components/BotRulesCard.js';
import { MetricChart } from '../components/MetricChart.js';
import { PageHeader } from '../components/PageHeader.js';
import { formatBytes, formatPercent } from '../lib/format.js';
import { botLabel, trafficLines } from '../lib/traffic.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { ThreatMapCard } from '../components/ThreatMapCard.js';
import { TrafficExplorer } from '../components/TrafficExplorer.js';
import { TrafficWorthALook } from '../components/TrafficWorthALook.js';
import { BanAction, type Actor } from '../components/traffic-parts.js';
import { SelectField } from '../ui/Field.js';

/**
 * Traffic (knowledge doc section 8.7, "Dashboard: Traffic page"; P6.5): who visits
 * the websites — people, bots that are who they say they are, and bots that are
 * not — for one server, a group or the fleet.
 *
 * Every figure is from the agents' per-minute summaries; no log line leaves a
 * server (rule 19). Recent ranges read minute rows; longer ones hourly rows, which
 * keep the top hundred keys of each list and no addresses — so the address list
 * never reaches back further than minute rows are kept.
 *
 * **Per-row actions** (section 8.7; P6.6), with one server chosen and for an
 * operator: Block, Rate-limit and Allow on a bot, Ban on an address — each shown
 * only when that server's web layer can enforce it (its heartbeat's
 * `capabilities.web`), because a firewall ban does nothing to a visitor behind
 * Cloudflare and Apache has no rate limiting of its own. Challenge is not offered:
 * section 20 leaves it out of v1.
 */

type Dialog =
  { kind: 'rule'; bot: BotCatalogEntry; action: BotRuleAction } | { kind: 'ban'; ip: string };

const ALL = '__all__';

const RANGES = [
  { value: '1', label: 'Last hour' },
  { value: '6', label: 'Last 6 hours' },
  { value: '24', label: 'Last day' },
  { value: '168', label: 'Last week' },
  { value: '720', label: 'Last 30 days' },
  { value: '2160', label: 'Last 90 days' },
] as const;

const count = (value: number): string => Math.round(value).toLocaleString('en-US');

const bucketWords = (seconds: number): string =>
  seconds < 3600
    ? `${String(seconds / 60)}-minute`
    : seconds < 86_400
      ? `${String(seconds / 3600)}-hour`
      : 'daily';

export function TrafficPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const [params, setParams] = useSearchParams();

  const serverId = params.get('server_id') ?? '';
  const groupId = params.get('group_id') ?? '';
  const hours = params.get('hours') ?? '24';

  /** A server and a group are one choice: picking one clears the other. */
  const set = (changes: Record<string, string>): void => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value === '' || value === ALL) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  /** Pinned when the range changes, so the query key settles (see `EventsPage`). */
  const from = useMemo(
    () => new Date(Date.now() - Number(hours) * 3_600_000).toISOString(),
    [hours],
  );
  const scope: TrafficScope = {
    ...(serverId === '' ? {} : { serverId }),
    ...(groupId === '' || serverId !== '' ? {} : { groupId }),
    from,
  };

  const series = useQuery({
    queryKey: ['traffic', scope],
    queryFn: () => getTrafficSeries(scope),
    placeholderData: keepPreviousData,
  });
  const servers = useQuery({ queryKey: ['servers'], queryFn: () => listServers({ limit: 200 }) });
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });

  const canAct = roleAtLeast(session.user.role, 'operator');
  const server = useQuery({
    queryKey: ['server', serverId],
    queryFn: () => getServer(serverId),
    enabled: serverId !== '',
  });
  const rules = useQuery({
    queryKey: ['web-rules', serverId],
    queryFn: () => getServerWebRules(serverId),
    enabled: serverId !== '',
  });
  const [dialog, setDialog] = useState<Dialog>();
  const chosen = serverId === '' ? undefined : server.data;
  const actor: Actor | undefined =
    chosen !== undefined && canAct
      ? {
          capabilities: chosen.capabilities?.web,
          effective: rules.data?.effective ?? {},
          onBot: (bot, action) => {
            setDialog({ kind: 'rule', bot, action });
          },
          onBan: (ip) => {
            setDialog({ kind: 'ban', ip });
          },
        }
      : undefined;

  return (
    <div className="page">
      <PageHeader
        title="Traffic"
        description="Who visits the websites on your servers: people, bots that are who they say they are, and bots that are not. Counted on each server from its own access log; the log lines never leave it."
      />

      <div className="filters">
        <SelectField
          label="Server"
          value={serverId === '' ? ALL : serverId}
          onChange={(event) => {
            set({ server_id: event.target.value, group_id: '' });
          }}
        >
          <option value={ALL}>Every server</option>
          {(servers.data?.items ?? []).map((server) => (
            <option key={server.id} value={server.id}>
              {server.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Group"
          value={groupId === '' || serverId !== '' ? ALL : groupId}
          onChange={(event) => {
            set({ group_id: event.target.value, server_id: '' });
          }}
        >
          <option value={ALL}>Any group</option>
          {(groups.data ?? []).map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Time"
          value={hours}
          onChange={(event) => {
            set({ hours: event.target.value === '24' ? '' : event.target.value });
          }}
        >
          {RANGES.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </SelectField>
      </div>

      {series.isError && (
        <Alert tone="danger" title="Could not load traffic">
          {series.error instanceof Error ? series.error.message : 'Try again in a moment.'}
        </Alert>
      )}

      {series.isPending && (
        <p className="row muted" aria-busy="true">
          <span className="spinner" aria-hidden="true" />
          Loading traffic…
        </p>
      )}

      {canAct && serverId === '' && (
        <p className="muted text-sm">
          Choose a server to block, rate-limit or ban from these lists; a rule can then apply to its
          group or to every server.
        </p>
      )}

      {series.data !== undefined && <RequestsCard data={series.data} timezone={timezone} />}

      <ThreatMapCard {...(serverId === '' ? {} : { serverId })} />

      {chosen !== undefined && rules.data !== undefined && (
        <BotRulesCard
          server={{ id: chosen.id, name: chosen.name, group: chosen.group }}
          rules={rules.data}
          capabilities={chosen.capabilities?.web}
          canChange={canAct}
        />
      )}

      <TrafficWorthALook scope={scope} actor={actor} canChange={canAct} />

      <TrafficExplorer
        scope={scope}
        actor={actor}
        canChange={canAct}
        siteActions={{
          serverId: serverId === '' ? undefined : serverId,
          // A ban reaches visitors only where this server can enforce one for them.
          onBan:
            actor?.capabilities?.webIpBan !== undefined && actor.capabilities.webIpBan !== 'none'
              ? actor.onBan
              : undefined,
        }}
      />

      <FakeBotsCard scope={scope} timezone={timezone} actor={actor} />

      {dialog?.kind === 'rule' && chosen !== undefined && (
        <BotRuleDialog
          server={{ id: chosen.id, name: chosen.name, group: chosen.group }}
          bot={dialog.bot}
          action={dialog.action}
          onClose={() => {
            setDialog(undefined);
          }}
        />
      )}
      {dialog?.kind === 'ban' && chosen !== undefined && (
        <ActionDialog
          type="ban_ip"
          server={{ id: chosen.id, name: chosen.name, group: chosen.group }}
          initialIp={dialog.ip}
          onClose={() => {
            setDialog(undefined);
          }}
        />
      )}
    </div>
  );
}

function RequestsCard({
  data,
  timezone,
}: {
  data: TrafficSeriesResponse;
  timezone: string;
}): ReactNode {
  const totals = data.points.reduce(
    (sum, point) => ({
      requests: sum.requests + point.requests,
      bytes: sum.bytes + point.bytes,
      human: sum.human + (point.categories.browser ?? 0),
      fake: sum.fake + (point.categories.fake_bot ?? 0),
      errors: sum.errors + point.status5xx,
      refused: sum.refused + point.status4xx,
    }),
    { requests: 0, bytes: 0, human: 0, fake: 0, errors: 0, refused: 0 },
  );
  const share = (part: number): string =>
    totals.requests === 0 ? '—' : formatPercent((part / totals.requests) * 100);

  return (
    <Card
      title="Requests"
      description={`Per ${bucketWords(data.bucketSeconds)} bucket${data.source === 'hour' ? ', from hourly totals' : ''}.`}
    >
      <dl className="traffic-totals">
        <div>
          <dt>Requests</dt>
          <dd>{count(totals.requests)}</dd>
        </div>
        <div>
          <dt>Likely human</dt>
          <dd>{share(totals.human)}</dd>
        </div>
        <div>
          <dt>Fake bots</dt>
          <dd>{count(totals.fake)}</dd>
        </div>
        <div>
          <dt>Refused (4xx)</dt>
          <dd>{share(totals.refused)}</dd>
        </div>
        <div>
          <dt>Server errors</dt>
          <dd>{share(totals.errors)}</dd>
        </div>
        <div>
          <dt>Sent</dt>
          <dd>{formatBytes(totals.bytes)}</dd>
        </div>
      </dl>
      <MetricChart
        title="Requests, people and bots"
        series={trafficLines(data)}
        from={data.from}
        to={data.to}
        bucketSeconds={data.bucketSeconds}
        format={count}
        seriesLabel={(line) => line.labels['line'] ?? ''}
        yMin={0}
        timezone={timezone}
        emptyText="No traffic in this range. A server counts it once its web environment is confirmed on its page and the Web traffic module is on."
      />
    </Card>
  );
}

function FakeBotsCard({
  scope,
  timezone,
  actor,
}: {
  scope: TrafficScope;
  timezone: string;
  actor?: Actor | undefined;
}): ReactNode {
  const filters = {
    ...(scope.serverId === undefined ? {} : { serverId: scope.serverId }),
    ...(scope.groupId === undefined ? {} : { groupId: scope.groupId }),
    result: 'failed' as const,
    limit: 20,
  };
  const fakes = useQuery({
    queryKey: ['bot-verifications', filters],
    queryFn: () => listBotVerifications(filters),
  });
  const items = fakes.data?.items ?? [];

  return (
    <Card
      title="Fake bots"
      description="Addresses that claimed to be a bot and failed its operator’s check, newest first. Kept 90 days after they were last seen."
    >
      {fakes.isError && <p className="muted text-sm">Could not load fake bots.</p>}
      {fakes.data !== undefined && items.length === 0 && (
        <p className="muted text-sm">
          None found. A claim fails only against catalog entries somebody has checked against the
          operator’s own documentation; until then a mismatch shows as unchecked.
        </p>
      )}
      {items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <caption className="visually-hidden">Addresses that failed a bot check</caption>
            <thead>
              <tr>
                <th scope="col">Address</th>
                <th scope="col">Claimed to be</th>
                <th scope="col">Last checked</th>
                <th scope="col">Seen on</th>
                {actor !== undefined && (
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <FakeBotRow
                  key={`${item.ip}-${item.claimedBot}`}
                  item={item}
                  timezone={timezone}
                  actor={actor}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {typeof fakes.data?.nextCursor === 'string' && (
        <p className="muted text-sm">Showing the {String(items.length)} most recent.</p>
      )}
    </Card>
  );
}

function FakeBotRow({
  item,
  timezone,
  actor,
}: {
  item: BotVerificationSummary;
  timezone: string;
  actor?: Actor | undefined;
}): ReactNode {
  const how =
    item.method === 'ip_ranges'
      ? 'not on the operator’s published list'
      : item.hostname === null
        ? 'no reverse name in the operator’s domain'
        : `reverse name ${item.hostname} does not point back`;
  return (
    <tr>
      <th scope="row">
        <code>{item.ip}</code>
      </th>
      <td>
        {botLabel(item.claimedBot)}
        <span className="cell-sub">{how}</span>
      </td>
      <td className="text-sm" title={formatInstant(item.lastChecked, { timezone })}>
        {formatRelative(item.lastChecked)}
        <span className="cell-sub">{`seen ${count(item.seenCount)} time${item.seenCount === 1 ? '' : 's'}`}</span>
      </td>
      <td className="text-sm">
        {item.serverIds.slice(0, 3).map((id, index) => (
          <span key={id}>
            {index > 0 && ', '}
            <Link to={`/servers/${encodeURIComponent(id)}`}>{id}</Link>
          </span>
        ))}
        {item.serverIds.length > 3 && ` and ${String(item.serverIds.length - 3)} more`}
      </td>
      {actor !== undefined && (
        <td>
          <BanAction ip={item.ip} actor={actor} />
        </td>
      )}
    </tr>
  );
}
